// B01–B08, B11, B12 — brand, marketing & communications tools.
import { useMemo, useState } from "react";
import { inr, pct } from "./engines";
import { Field, ResultRow, ToolSection, Note, copyText } from "./ui";

function CopyButton({ getText, testid }) {
  const [state, setState] = useState("idle");
  return (
    <button type="button" data-testid={testid}
      onClick={async () => { setState((await copyText(getText())) ? "done" : "fail"); setTimeout(() => setState("idle"), 2500); }}
      className="text-sm font-medium underline underline-offset-4">
      {state === "done" ? "Copied" : state === "fail" ? "Select & copy manually" : "Copy output"}
    </button>
  );
}

/* ---------- B01 Positioning Stress Test ---------- */
const B01_TESTS = [
  { key: "swap", q: "The swap test", hint: "Put a competitor's name on your positioning statement. If it still works, it positions nobody.", fail: "A rival could say this verbatim" },
  { key: "enemy", q: "The enemy test", hint: "Strong positioning is against something — an old way, a compromise, a category lie.", fail: "Nothing is being opposed" },
  { key: "proof", q: "The proof test", hint: "Name the three receipts: the facts, numbers or demos that make the claim undeniable.", fail: "Claims outnumber proofs" },
  { key: "sacrifice", q: "The sacrifice test", hint: "What do you deliberately NOT offer, serve or chase? Positioning without sacrifice is a brochure.", fail: "Everything is for everyone" },
  { key: "repsay", q: "The salesperson test", hint: "Can a rep say it on a call without slides, in one breath, and be believed?", fail: "Only marketing can say it" },
  { key: "customer", q: "The customer-mirror test", hint: "Would your best customer recognise themselves in it — and repeat it to a peer?", fail: "Customers describe you differently" },
];
export function ToolB01() {
  const [scores, setScores] = useState({});
  const [stmt, setStmt] = useState("");
  const done = Object.keys(scores).length;
  const passed = Object.values(scores).filter((v) => v === 1).length;
  const failed = B01_TESTS.filter((t, i) => scores[i] === 0);
  return (
    <div data-testid="tool-B01" className="grid lg:grid-cols-2 gap-10">
      <div className="space-y-4">
        <label className="block">
          <span className="text-sm font-medium text-zinc-800">Your positioning statement</span>
          <textarea value={stmt} onChange={(e) => setStmt(e.target.value)} rows={3} placeholder="For [customer] who [need], we are the [frame] that [difference], because [proof]." className="mt-1.5 w-full border border-zinc-300 p-3 text-sm outline-none focus:border-black" data-testid="b01-statement" />
        </label>
        {B01_TESTS.map((t, i) => (
          <div key={t.key} className="border border-zinc-200 p-4" data-testid={`b01-${t.key}`}>
            <div className="text-sm font-medium">{t.q}</div>
            <p className="text-xs text-zinc-500 mt-1">{t.hint}</p>
            <div className="mt-3 flex gap-2">
              {[["Pass", 1], ["Fail", 0]].map(([label, v]) => (
                <button key={label} type="button" onClick={() => setScores((s) => ({ ...s, [i]: v }))}
                  className={`px-4 py-1.5 text-xs border transition-colors ${scores[i] === v ? (v ? "border-black bg-black text-white" : "border-red-700 bg-red-700 text-white") : "border-zinc-300 hover:border-black"}`}
                  data-testid={`b01-${t.key}-${v}`}>{label}</button>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div>
        {done === B01_TESTS.length ? (
          <div data-testid="b01-result">
            <ResultRow label="Positioning strength" value={`${passed} / ${B01_TESTS.length} tests passed`} strong tone={passed >= 5 ? "good" : passed >= 3 ? undefined : "bad"} />
            {failed.length > 0 && (
              <div className="mt-5">
                <div className="text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">Where it breaks</div>
                <ul className="list-disc pl-5 space-y-1.5 text-sm text-zinc-700">
                  {failed.map((f) => <li key={f.key}><strong>{f.q}:</strong> {f.fail}</li>)}
                </ul>
              </div>
            )}
            <Note tone={passed >= 5 ? "info" : "warn"}>{passed >= 5 ? "Strong. Now validate: read it to five recent buyers and five lost prospects. Their faces will tell you more than this tool can." : "Rewrite around the failed tests — usually the fix is choosing an enemy and accepting a sacrifice. Then re-run."}</Note>
          </div>
        ) : <Note>Run all six tests. {done}/6 done.</Note>}
      </div>
    </div>
  );
}

/* ---------- B02 Value Proposition Builder ---------- */
const B02_PARTS = [
  ["job", "Customer job", "What are they trying to get done? Their words."],
  ["pain", "The pain of today's way", "What does the current approach cost them?"],
  ["promise", "Our promise", "The outcome we deliver — not the product."],
  ["proof", "Proof", "Three receipts: numbers, cases, demos."],
  ["difference", "Only-we", "What can we claim that rivals can't copy by Tuesday?"],
];
export function ToolB02() {
  const [v, setV] = useState({ job: "", pain: "", promise: "", proof: "", difference: "" });
  const checks = useMemo(() => ({
    sayable: (v.promise.match(/\s/g) || []).length <= 18 && v.promise.trim().length > 0,
    provable: v.proof.trim().length > 20,
    ownable: v.difference.trim().length > 10,
    customerWords: v.job.trim().length > 10,
  }), [v]);
  const passed = Object.values(checks).filter(Boolean).length;
  return (
    <div data-testid="tool-B02" className="grid lg:grid-cols-2 gap-10">
      <div className="space-y-4">
        {B02_PARTS.map(([k, label, hint]) => (
          <label key={k} className="block">
            <span className="text-sm font-medium text-zinc-800">{label}</span>
            <span className="block text-xs text-zinc-500 mt-0.5">{hint}</span>
            <textarea value={v[k]} onChange={(e) => setV((s) => ({ ...s, [k]: e.target.value }))} rows={2} className="mt-1.5 w-full border border-zinc-300 p-2.5 text-sm outline-none focus:border-black" data-testid={`b02-${k}`} />
          </label>
        ))}
      </div>
      <div>
        <div className="border-2 border-black p-6 bg-white" data-testid="b02-preview">
          <div className="text-xs uppercase tracking-widest text-zinc-500">The proposition, assembled</div>
          <p className="mt-3 font-serif-display text-xl leading-snug">
            {v.promise || <span className="text-zinc-300 italic">Your promise appears here…</span>}
          </p>
          <p className="mt-3 text-sm text-zinc-600 leading-relaxed">
            For customers whose job is {v.job || "…"} and who today lose {v.pain || "…"}. We prove it: {v.proof || "…"} And unlike alternatives: {v.difference || "…"}
          </p>
        </div>
        <div className="mt-5" data-testid="b02-checks">
          <div className="text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">Claim checks</div>
          <ResultRow label="Sayable in one breath (≤18 words)" value={checks.sayable ? "Pass" : "Fail"} tone={checks.sayable ? "good" : "bad"} />
          <ResultRow label="Proof exists (receipts written)" value={checks.provable ? "Pass" : "Fail"} tone={checks.provable ? "good" : "bad"} />
          <ResultRow label="Ownable difference" value={checks.ownable ? "Pass" : "Fail"} tone={checks.ownable ? "good" : "bad"} />
          <ResultRow label="Strength" value={`${passed}/4`} strong />
        </div>
        <Note>Validation question for your next five customer calls: 'When you chose us, what were you really hiring us to do?' If their answer differs from your proposition, theirs wins.</Note>
      </div>
    </div>
  );
}

/* ---------- B03 Distinctiveness & Blind-Date Test ---------- */
const B03_ASSETS = [
  ["colour", "Colour palette", "Could a stranger pick yours from a lineup of category packs?"],
  ["shape", "Shape / layout", "Pack silhouette, page layout, deck structure — recognisably yours?"],
  ["language", "Tone & phrases", "Tagline, vocabulary, the way you open a proposal."],
  ["sonic", "Sound / motion", "Audio logo, animation style, hold music, intro sting."],
  ["character", "Mascot / face / motif", "A recurring character, founder's face, visual motif."],
  ["ritual", "Rituals & formats", "The way you onboard, present, package, deliver."],
];
export function ToolB03() {
  const [scores, setScores] = useState({});
  const done = Object.keys(scores).length;
  const total = Object.values(scores).reduce((a, b) => a + b, 0);
  const weak = B03_ASSETS.filter((_, i) => (scores[i] || 0) < 2);
  return (
    <div data-testid="tool-B03" className="grid lg:grid-cols-2 gap-10">
      <div className="space-y-3">
        <p className="text-sm text-zinc-600 mb-2">The blind-date test: strip your logo from your ad, pack, site and deck. Would anyone know it's you? Score each asset 0–2: 0 = generic, 1 = somewhat ours, 2 = unmistakably ours.</p>
        {B03_ASSETS.map(([k, label, hint], i) => (
          <div key={k} className="border border-zinc-200 p-3" data-testid={`b03-${k}`}>
            <div className="flex items-center justify-between gap-4">
              <div>
                <div className="text-sm font-medium">{label}</div>
                <div className="text-xs text-zinc-500">{hint}</div>
              </div>
              <div className="flex gap-1.5">
                {[0, 1, 2].map((v) => (
                  <button key={v} type="button" onClick={() => setScores((s) => ({ ...s, [i]: v }))}
                    className={`w-8 h-8 text-xs border transition-colors ${scores[i] === v ? "border-black bg-black text-white" : "border-zinc-300 hover:border-black"}`}
                    data-testid={`b03-${i}-${v}`}>{v}</button>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
      <div>
        {done === B03_ASSETS.length ? (
          <div data-testid="b03-result">
            <ResultRow label="Distinctiveness score" value={`${total} / 12`} strong tone={total >= 9 ? "good" : total >= 6 ? undefined : "bad"} />
            <div className="mt-5">
              <div className="text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">Build these assets first</div>
              <ul className="list-disc pl-5 space-y-1.5 text-sm text-zinc-700">
                {weak.map(([k, label]) => <li key={k}>{label}</li>)}
                {weak.length === 0 && <li>Every asset is distinctive — rare and valuable. Protect them in brand guidelines with teeth.</li>}
              </ul>
            </div>
            <ToolSection title="The recall test to run">
              <p className="text-sm text-zinc-600 leading-relaxed">Show ten category buyers your debranded ad and three rivals', five seconds each. Ask: whose is this? Under 60% correct means you're funding memory for the category, not for you.</p>
            </ToolSection>
          </div>
        ) : <Note>Score all six asset classes. {done}/6 done.</Note>}
      </div>
    </div>
  );
}

/* ---------- B04 Brand Architecture Decision Tree ---------- */
const B04_TREE = {
  start: {
    q: "Is the new offer bought by the same customer, for the same job, at a similar price point?",
    yes: "endorsed", no: "q2",
  },
  q2: {
    q: "Does the new offer carry meaningful risk to the master brand's reputation if it fails (category, quality, channel)?",
    yes: "newbrand", no: "q3",
  },
  q3: {
    q: "Will buyers of the new offer actively research and compare it separately (own search terms, own consideration set)?",
    yes: "subbrand", no: "descriptive",
  },
  endorsed: { verdict: "Endorsed / range extension", why: "Same customer, same job, similar price: the master brand's trust transfers and marketing cost stays shared. Format: 'MasterBrand — Range Name'.", risk: "Watch: stretching into lower price points quietly reprices the master brand.", examples: "How it reads: credibility borrowed, novelty flagged." },
  descriptive: { verdict: "Descriptive new name, lightly endorsed", why: "A different customer, job or price point means the master brand's promise doesn't transfer — borrowing it reads as confusing, not credible. With low reputational risk and no separate research behaviour, a standalone brand is overkill: give the offer its own descriptive name with a small 'from MasterBrand' endorsement.", risk: "Watch: if buyers begin researching this category separately, revisit — that is the trigger for a true sub-brand.", examples: "How it reads: the new name leads; 'from MasterBrand' sits small beneath." },
  subbrand: { verdict: "Sub-brand", why: "A distinct buying process deserves its own identity — but keep the master brand visible as endorser to import trust at half the launch cost.", risk: "Watch: sub-brands multiply. Each one is a permanent marketing budget line.", examples: "How it reads: its own name, 'by MasterBrand' beneath." },
  newbrand: { verdict: "New standalone brand", why: "Genuine reputational risk or a fundamentally different customer argues for separation. Expensive, slow — but failure doesn't contaminate the mothership.", risk: "Watch: standalone brands need their own distribution economics to survive.", examples: "How it reads: no visible link; legal entity separation optional." },
};
export function ToolB04() {
  const [node, setNode] = useState("start");
  const [trail, setTrail] = useState([]);
  const n = B04_TREE[node];
  const answer = (a) => {
    setTrail((t) => [...t, { q: n.q, a }]);
    setNode(n[a]);
  };
  const reset = () => { setNode("start"); setTrail([]); };
  return (
    <div data-testid="tool-B04">
      <div className="border-2 border-black p-6 bg-white" data-testid="b04-node">
        {!n.verdict ? (
          <>
            <div className="text-xs uppercase tracking-widest text-zinc-500">Question {trail.length + 1} of up to 3</div>
            <p className="mt-3 font-serif-display text-xl leading-snug">{n.q}</p>
            <div className="mt-5 flex gap-3">
              <button type="button" onClick={() => answer("yes")} className="px-6 py-2.5 bg-black text-white text-sm hover:bg-zinc-800 transition-colors" data-testid="b04-yes">Yes</button>
              <button type="button" onClick={() => answer("no")} className="px-6 py-2.5 border border-zinc-300 text-sm hover:border-black transition-colors" data-testid="b04-no">No</button>
            </div>
          </>
        ) : (
          <>
            <div className="text-xs uppercase tracking-widest text-zinc-500">Recommendation</div>
            <p className="mt-3 font-serif-display text-2xl leading-snug">{n.verdict}</p>
            <p className="mt-3 text-sm text-zinc-700 leading-relaxed">{n.why}</p>
            <p className="mt-2 text-sm text-zinc-500">{n.examples}</p>
            <p className="mt-3 text-sm text-red-800">{n.risk}</p>
            <button type="button" onClick={reset} className="mt-5 text-sm font-medium underline underline-offset-4" data-testid="b04-restart">Test another offer</button>
          </>
        )}
      </div>
      {trail.length > 0 && !n.verdict && (
        <div className="mt-4 space-y-1.5" data-testid="b04-trail">
          {trail.map((t, i) => <div key={i} className="text-xs text-zinc-500"><span className="font-medium text-zinc-700">{t.a.toUpperCase()}</span> — {t.q}</div>)}
        </div>
      )}
      <Note>Architecture debates end when the questions are about the buyer and the risk — not about anyone's favourite logo. Whatever you choose, write the naming rules down the same day.</Note>
    </div>
  );
}

/* ---------- B05 Brand-to-Revenue Canvas ---------- */
const B05_BLOCKS = [
  ["promise", "The brand promise", "One sentence a customer would repeat"],
  ["audience", "Whose memory it must live in", "The buying audience, not 'everyone'"],
  ["behaviour", "The buying behaviour it must change", "Choose us first / pay more / switch / stay"],
  ["metric", "The commercial metric it moves", "Win rate, price premium, repeat %, CAC"],
  ["proof", "Evidence the link works", "Cohort data, brand-track correlation, pricing tests"],
  ["investment", "What we'll consistently fund", "The assets and presence that build memory over years"],
];
export function ToolB05() {
  const [v, setV] = useState({ promise: "", audience: "", behaviour: "", metric: "", proof: "", investment: "" });
  const filled = Object.values(v).filter((x) => x.trim()).length;
  return (
    <div data-testid="tool-B05">
      <p className="text-sm text-zinc-600 mb-5">Connect the brand promise to the sales number it's supposed to move. If the canvas can't name the behaviour and the metric, the brand spend is hope, not investment.</p>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {B05_BLOCKS.map(([k, label, hint]) => (
          <div key={k} className={`border p-4 transition-colors ${v[k].trim() ? "border-zinc-200" : "border-dashed border-red-300"}`} data-testid={`b05-${k}`}>
            <div className="text-sm font-medium">{label}</div>
            <p className="text-xs text-zinc-500 mt-1">{hint}</p>
            <textarea value={v[k]} onChange={(e) => setV((s) => ({ ...s, [k]: e.target.value }))} rows={3} className="mt-2 w-full border border-zinc-200 p-2 text-sm outline-none focus:border-black" data-testid={`b05-input-${k}`} />
          </div>
        ))}
      </div>
      <div className="mt-5 flex items-center gap-6" data-testid="b05-result">
        <ResultRow label="Canvas completeness" value={`${filled}/6`} strong={filled === 6} />
        <CopyButton testid="b05-copy" getText={() => `BRAND-TO-REVENUE CANVAS\n\n${B05_BLOCKS.map(([k, l]) => `${l.toUpperCase()}\n${v[k] || "— GAP —"}`).join("\n\n")}`} />
      </div>
    </div>
  );
}

/* ---------- B06 Category Story & Messaging Framework ---------- */
const B06_ARC = [
  ["oldway", "The old way", "How buyers solve this today — and its hidden tax"],
  ["shift", "The shift", "What changed in the world that makes the old way obsolete"],
  ["stakes", "The stakes", "What it costs to stay behind — quantified"],
  ["newway", "The new way", "The category answer, before your product appears"],
  ["us", "Why us", "Now, and only now, your product as the proof"],
];
export function ToolB06() {
  const [v, setV] = useState({ oldway: "", shift: "", stakes: "", newway: "", us: "" });
  const [objections, setObjections] = useState([{ o: "Too expensive", r: "" }, { o: "We already have a workaround", r: "" }, { o: "Too risky to switch", r: "" }]);
  const filled = Object.values(v).filter((x) => x.trim()).length;
  return (
    <div data-testid="tool-B06">
      <div className="grid lg:grid-cols-2 gap-10">
        <div className="space-y-4">
          <p className="text-sm text-zinc-600">Build the story that makes the old way of buying feel outdated. The product enters last — stories that open with the product are brochures.</p>
          {B06_ARC.map(([k, label, hint]) => (
            <label key={k} className="block">
              <span className="text-sm font-medium text-zinc-800">{label}</span>
              <span className="block text-xs text-zinc-500 mt-0.5">{hint}</span>
              <textarea value={v[k]} onChange={(e) => setV((s) => ({ ...s, [k]: e.target.value }))} rows={2} className="mt-1.5 w-full border border-zinc-300 p-2.5 text-sm outline-none focus:border-black" data-testid={`b06-${k}`} />
            </label>
          ))}
        </div>
        <div>
          <div className="border-2 border-black p-6 bg-white" data-testid="b06-preview">
            <div className="text-xs uppercase tracking-widest text-zinc-500">Your narrative arc · {filled}/5</div>
            {B06_ARC.map(([k, label]) => (
              <div key={k} className="mt-3">
                <div className="text-xs font-medium uppercase tracking-widest text-zinc-400">{label}</div>
                <p className="mt-0.5 text-sm text-zinc-800 leading-relaxed">{v[k] || <span className="italic text-zinc-300">Not written</span>}</p>
              </div>
            ))}
          </div>
          <ToolSection title="Objection-proof table" testid="b06-objections">
            <div className="space-y-3">
              {objections.map((o, i) => (
                <div key={i} className="grid md:grid-cols-2 gap-3 border border-zinc-200 p-3" data-testid={`b06-obj-${i}`}>
                  <input value={o.o} onChange={(e) => setObjections((s) => s.map((x, xi) => (xi === i ? { ...x, o: e.target.value } : x)))} className="text-sm font-medium outline-none border-b border-transparent focus:border-black" placeholder="Objection" />
                  <input value={o.r} onChange={(e) => setObjections((s) => s.map((x, xi) => (xi === i ? { ...x, r: e.target.value } : x)))} className="text-sm outline-none border-b border-transparent focus:border-black" placeholder="Our proof-based response" />
                </div>
              ))}
              <button type="button" onClick={() => setObjections((s) => [...s, { o: "", r: "" }])} className="text-sm font-medium underline underline-offset-4" data-testid="b06-add-obj">+ Add objection</button>
            </div>
          </ToolSection>
        </div>
      </div>
    </div>
  );
}

/* ---------- B07 Creative Pre-Flight Scorecard (9 checks) ---------- */
const B07_CHECKS = [
  "Branded in the first 3 seconds / first glance",
  "One message, not three",
  "About the buyer's problem before our product",
  "Distinctively ours — survives the logo-strip test",
  "A specific, provable claim (not 'best', 'leading', 'quality')",
  "A reason to act now that isn't a fake countdown",
  "Readable at actual size, at actual speed, on an actual phone",
  "One clear next step",
  "Would survive being shown to a sceptical CFO",
];
export function ToolB07() {
  const [scores, setScores] = useState({});
  const done = Object.keys(scores).length;
  const passed = Object.values(scores).filter(Boolean).length;
  const failed = B07_CHECKS.filter((_, i) => scores[i] === false);
  return (
    <div data-testid="tool-B07" className="grid lg:grid-cols-2 gap-10">
      <div className="space-y-3">
        <p className="text-sm text-zinc-600 mb-2">Before the campaign spends a rupee: pass/fail each check on the actual creative — not the strategy deck.</p>
        {B07_CHECKS.map((c, i) => (
          <div key={i} className="border border-zinc-200 p-3 flex items-center justify-between gap-4" data-testid={`b07-check-${i}`}>
            <span className="text-sm text-zinc-700 flex-1">{c}</span>
            <div className="flex gap-1.5">
              <button type="button" onClick={() => setScores((s) => ({ ...s, [i]: true }))} className={`px-3 h-8 text-xs border transition-colors ${scores[i] === true ? "border-black bg-black text-white" : "border-zinc-300 hover:border-black"}`} data-testid={`b07-pass-${i}`}>Pass</button>
              <button type="button" onClick={() => setScores((s) => ({ ...s, [i]: false }))} className={`px-3 h-8 text-xs border transition-colors ${scores[i] === false ? "border-red-700 bg-red-700 text-white" : "border-zinc-300 hover:border-black"}`} data-testid={`b07-fail-${i}`}>Fail</button>
            </div>
          </div>
        ))}
      </div>
      <div>
        {done === B07_CHECKS.length ? (
          <div data-testid="b07-result">
            <ResultRow label="Pre-flight score" value={`${passed} / 9`} strong tone={passed >= 8 ? "good" : passed >= 6 ? undefined : "bad"} />
            <ResultRow label="Verdict" value={passed === 9 ? "Cleared for spend" : passed >= 7 ? "Fix the failures first — small revision" : "Do not fly — structural revision"} tone={passed === 9 ? "good" : "bad"} />
            {failed.length > 0 && (
              <div className="mt-5">
                <div className="text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">Revision checklist (ranked)</div>
                <ol className="list-decimal pl-5 space-y-1.5 text-sm text-zinc-700">{failed.map((f, i) => <li key={i}>{f}</li>)}</ol>
              </div>
            )}
            <ToolSection title="The cheap test">
              <p className="text-sm text-zinc-600 leading-relaxed">Show the creative to five people from the target audience for five seconds. Ask: whose ad, what's the message, what should you do? Three wrong answers beats three months of post-campaign analysis.</p>
            </ToolSection>
          </div>
        ) : <Note>Score all nine checks. {done}/9 done.</Note>}
      </div>
    </div>
  );
}

/* ---------- B08 Marketing Waste Detector — with the mandated composite case ---------- */
const B08_HYPOTHESES = [
  { key: "vanity", label: "Vanity metrics substituted for outcomes", signal: "Reports lead with impressions, reach, engagement — not sales, trials or contribution." },
  { key: "noctrl", label: "No holdout or control", signal: "Every claimed effect could be seasonality; nothing was measured against a group that didn't get the campaign." },
  { key: "wrongaud", label: "Right creative, wrong audience", signal: "The people who loved it are not the people who buy it." },
  { key: "nobridge", label: "No bridge to purchase", signal: "The campaign ends at awareness; no offer, no trail to store, site or salesperson." },
  { key: "credit", label: "Sales would have happened anyway", signal: "Campaign period sales match the trend line; the campaign claimed the weather." },
  { key: "onetime", label: "One-off spike, no memory built", signal: "Numbers returned to baseline within weeks; nothing distinctive was repeated long enough to compound." },
];
export function ToolB08() {
  const [ratings, setRatings] = useState({});
  const done = Object.keys(ratings).length;
  const ranked = B08_HYPOTHESES.map((h, i) => ({ ...h, r: ratings[i] ?? null }))
    .filter((h) => h.r !== null)
    .sort((a, b) => b.r - a.r);
  return (
    <div data-testid="tool-B08">
      {/* Mandated composite case (spec) — illustrative, labelled */}
      <div className="border-2 border-black p-6 bg-zinc-50" data-testid="b08-case">
        <div className="text-xs uppercase tracking-widest text-zinc-500">Composite case — illustrative figures, not a client outcome</div>
        <h3 className="mt-2 font-serif-display text-2xl">The Campaign Everyone Loved but Nobody Bought</h3>
        <div className="mt-4 grid md:grid-cols-2 gap-6 text-sm text-zinc-700 leading-relaxed">
          <div className="space-y-3">
            <p>A consumer brand spends <strong>₹45 L</strong> on a festive campaign: a warm film, a celebrity cameo, 40 million impressions. Internally, it is adored. The agency wins a shortlist. The CEO's family forwards it.</p>
            <p>The quarter's numbers: sales in campaign cities <strong>+2%</strong> — against <strong>+4%</strong> in matched non-campaign cities. Brand search volume: flat by week 6. Repeat purchase among 'engaged' audiences: indistinguishable from control.</p>
          </div>
          <div className="space-y-3">
            <p>What the review found: no holdout was planned (<em>measurement gap</em>); the film tested best with 18–24s while buyers are 35–50 (<em>wrong audience</em>); and the ad ended with a feeling, not a next step (<em>no bridge</em>).</p>
            <p>The fix wasn't a better film. It was a measurement plan agreed before spend, an audience built from purchase data, and a creative brief that ends in an action. The next campaign spent ₹28 L and returned a measurable 1.9× contribution.</p>
          </div>
        </div>
      </div>
      <ToolSection title="Run the diagnostic on your own last campaign" testid="b08-diag">
        <p className="text-sm text-zinc-600 mb-4">Rate the evidence for each inefficiency hypothesis: 0 = no evidence, 1 = some, 2 = strong.</p>
        <div className="space-y-3">
          {B08_HYPOTHESES.map((h, i) => (
            <div key={h.key} className="border border-zinc-200 p-4" data-testid={`b08-${h.key}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="text-sm font-medium">{h.label}</div>
                  <div className="text-xs text-zinc-500 mt-1">{h.signal}</div>
                </div>
                <div className="flex gap-1.5">
                  {[0, 1, 2].map((v) => (
                    <button key={v} type="button" onClick={() => setRatings((s) => ({ ...s, [i]: v }))}
                      className={`w-8 h-8 text-xs border transition-colors ${ratings[i] === v ? "border-black bg-black text-white" : "border-zinc-300 hover:border-black"}`}
                      data-testid={`b08-${i}-${v}`}>{v}</button>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
        {done === B08_HYPOTHESES.length && (
          <div className="mt-6 max-w-lg" data-testid="b08-result">
            <div className="text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">Ranked by evidence</div>
            {ranked.map((h) => (
              <ResultRow key={h.key} label={h.label} value={["no evidence", "some evidence", "strong evidence"][h.r]} tone={h.r === 2 ? "bad" : undefined} strong={h === ranked[0]} />
            ))}
            <Note tone="warn">Fix the top hypothesis before the next budget cycle. And whatever the ranking: agree the measurement plan — holdout, metric, decision date — before the next rupee moves.</Note>
          </div>
        )}
      </ToolSection>
    </div>
  );
}

/* ---------- B11 Content Authority & Topic Pillars ---------- */
export function ToolB11() {
  const [pillars, setPillars] = useState([
    { question: "How do I choose between X and Y?", content: "", metric: "" },
    { question: "What does this actually cost, all-in?", content: "", metric: "" },
    { question: "What goes wrong, and how do I avoid it?", content: "", metric: "" },
  ]);
  const setP = (i, k) => (e) => setPillars((s) => s.map((x, xi) => (xi === i ? { ...x, [k]: e.target.value } : x)));
  const complete = pillars.filter((p) => p.question.trim() && p.content.trim()).length;
  return (
    <div data-testid="tool-B11">
      <p className="text-sm text-zinc-600 mb-5">Buyers ask the same five questions before they buy anything in your category. Own them. Each pillar: the question, the definitive content you'll build, and the lead metric that proves it works.</p>
      <div className="space-y-4">
        {pillars.map((p, i) => (
          <div key={i} className="border border-zinc-200 p-4 grid md:grid-cols-3 gap-4" data-testid={`b11-pillar-${i}`}>
            <label className="block">
              <span className="text-xs font-medium uppercase tracking-widest text-zinc-500">The question buyers ask</span>
              <input value={p.question} onChange={setP(i, "question")} className="mt-1.5 w-full border border-zinc-200 px-2 py-1.5 text-sm outline-none focus:border-black" data-testid={`b11-q-${i}`} />
            </label>
            <label className="block">
              <span className="text-xs font-medium uppercase tracking-widest text-zinc-500">The pillar content we build</span>
              <textarea value={p.content} onChange={setP(i, "content")} rows={2} placeholder="Guide, calculator, comparison, video series…" className="mt-1.5 w-full border border-zinc-200 p-2 text-sm outline-none focus:border-black" data-testid={`b11-c-${i}`} />
            </label>
            <label className="block">
              <span className="text-xs font-medium uppercase tracking-widest text-zinc-500">Lead metric (not views)</span>
              <input value={p.metric} onChange={setP(i, "metric")} placeholder="e.g. demo requests from this pillar" className="mt-1.5 w-full border border-zinc-200 px-2 py-1.5 text-sm outline-none focus:border-black" data-testid={`b11-m-${i}`} />
            </label>
          </div>
        ))}
      </div>
      <div className="mt-5 flex items-center gap-6">
        <button type="button" onClick={() => setPillars((s) => [...s, { question: "", content: "", metric: "" }])} className="text-sm font-medium underline underline-offset-4" data-testid="b11-add">+ Add pillar</button>
        <CopyButton testid="b11-copy" getText={() => `CONTENT AUTHORITY PLAN\n\n${pillars.map((p, i) => `${i + 1}. ${p.question}\n   Build: ${p.content || "—"}\n   Metric: ${p.metric || "—"}`).join("\n")}`} />
      </div>
      <div className="mt-4 max-w-md" data-testid="b11-result">
        <ResultRow label="Pillars defined" value={`${complete} / ${pillars.length}`} strong={complete === pillars.length && pillars.length >= 3} />
      </div>
      <Note tone="warn">Five questions, owned for two years, beats fifty posts nobody remembers. Measure pillars by the leads and sales conversations they start — traffic is a means, not the metric.</Note>
    </div>
  );
}

/* ---------- B12 Crisis Playbook (branching + timeline + holding statements) ---------- */
const B12_SCENARIOS = {
  start: {
    q: "It's 9:12 AM. A video of a product quality failure is spreading — 40K views and climbing. What's the first verified fact you need?",
    choices: [
      { label: "Is the claim true — is it our product, our batch, our fault?", to: "verify", good: true },
      { label: "How do we get the video taken down?", to: "takedown", good: false },
      { label: "Who approved this product launch, internally?", to: "blame", good: false },
    ],
  },
  verify: {
    q: "Verified by 9:40: it is your batch, a real defect, one reported injury risk. What goes out first?",
    choices: [
      { label: "A holding statement acknowledging, with the safety instruction and a timestamp for the next update", to: "holding", good: true },
      { label: "Silence until legal reviews everything", to: "silence", good: false },
      { label: "A denial pending 'full investigation', plus the ad campaign continues", to: "denial", good: false },
    ],
  },
  takedown: { q: "Outcome: the platform declines; the attempt leaks ('brand tries to silence customer'). The story is now the cover-up. The first hour was spent on the wrong problem.", terminal: true, lesson: "Minute 0–15 belongs to verification and safety, never to suppression. Suppression attempts become the story." },
  blame: { q: "Outcome: two hours lost to internal alignment while journalists fill the vacuum. By noon, a competitor's founder is on TV explaining 'what responsible brands do'.", terminal: true, lesson: "Assign blame after the fire is out. In the first hour there is exactly one audience: the people at risk." },
  silence: { q: "Outcome: 'No response from the company' becomes the second headline. By the time legal clears a statement at 6 PM, the narrative is set without you.", terminal: true, lesson: "A holding statement is not an admission — it's proof you're awake. Say what you know, what you're doing, and when you'll update." },
  denial: { q: "Outcome: the batch number is visible in the video. The denial collapses by evening, and now there are two stories: the defect and the lie.", terminal: true, lesson: "Never deny what you haven't verified. 'We're investigating and here's what customers should do now' is always available." },
  holding: { q: "The holding statement lands by 10:30: acknowledge, safety instruction, refund channel, next update at 2 PM. The story shrinks to the facts. Final step for the week?", choices: [
    { label: "Publish the batch-recall scope and the fix, then the 30-day quality report", to: "resolve", good: true },
    { label: "Quietly refund complainers and wait for it to blow over", to: "quiet", good: false },
  ] },
  resolve: { q: "Outcome: coverage by day 3 is 'brand handled it right'. Trust metrics dip and recover within the quarter — because the response was faster than the outrage.", terminal: true, lesson: "Speed + safety + a dated next update is the whole playbook. Everything else is decoration." },
  quiet: { q: "Outcome: a refund recipient posts the settlement demand letter. 'Brand pays off victims quietly' — the story returns, worse, in week 3.", terminal: true, lesson: "Quiet settlements read as cover-ups. Publish the scope and the fix once, loudly." },
};
const B12_TIMELINE = [
  ["0–15 min", "Verify: is it true, is it ours, is anyone at risk? Activate the named crisis owner."],
  ["15–45 min", "Safety instruction drafted and approved by the pre-agreed pair (ops + comms). Legal is consulted, not in charge."],
  ["45–60 min", "Holding statement out on owned channels: acknowledge, instruct, commit to the next update time."],
  ["Hour 2–24", "Spokesperson briefed; customer support scripted; media log started; update published exactly when promised."],
];
export function ToolB12() {
  const [node, setNode] = useState("start");
  const [lessons, setLessons] = useState([]);
  const n = B12_SCENARIOS[node];
  const choose = (c) => { setNode(c.to); if (!c.good) setLessons((l) => [...l, B12_SCENARIOS[c.to].lesson]); };
  const reset = () => { setNode("start"); setLessons([]); };
  return (
    <div data-testid="tool-B12">
      <div className="grid lg:grid-cols-2 gap-10">
        <div>
          <div className="border-2 border-black p-6 bg-white" data-testid="b12-node">
            <div className="text-xs uppercase tracking-widest text-zinc-500">{n.terminal ? "Debrief" : "Scenario: the first 60 minutes"}</div>
            <p className="mt-3 font-serif-display text-lg leading-snug">{n.q}</p>
            {n.choices && (
              <div className="mt-5 space-y-2">
                {n.choices.map((c, i) => (
                  <button key={i} type="button" onClick={() => choose(c)} className="block w-full text-left border border-zinc-300 p-3.5 text-sm hover:border-black transition-colors" data-testid={`b12-choice-${i}`}>{c.label}</button>
                ))}
              </div>
            )}
            {n.terminal && (
              <>
                <p className="mt-3 text-sm text-zinc-600">{n.lesson}</p>
                <button type="button" onClick={reset} className="mt-4 text-sm font-medium underline underline-offset-4" data-testid="b12-restart">Run the hour again</button>
              </>
            )}
          </div>
          {lessons.length > 0 && !n.terminal && (
            <div className="mt-4 space-y-2" data-testid="b12-lessons">
              {lessons.map((l, i) => <Note key={i} tone="warn">{l}</Note>)}
            </div>
          )}
        </div>
        <div>
          <div className="text-xs font-medium uppercase tracking-widest text-zinc-500 mb-3">The standing playbook — agreed before you need it</div>
          <div className="space-y-3" data-testid="b12-timeline">
            {B12_TIMELINE.map(([t, action]) => (
              <div key={t} className="flex gap-4 border border-zinc-200 p-3.5">
                <span className="w-20 shrink-0 text-xs font-semibold uppercase tracking-wide text-black pt-0.5">{t}</span>
                <span className="text-sm text-zinc-700 leading-relaxed">{action}</span>
              </div>
            ))}
          </div>
          <ToolSection title="Holding-statement skeleton (fill the brackets now)">
            <div className="border border-zinc-200 bg-zinc-50 p-4 text-sm text-zinc-700 leading-relaxed" data-testid="b12-holding">
              "We are aware of [incident] affecting [product/batch]. If you have this product, [safety instruction]. We are investigating now and will update here by [time]. Refunds and replacements: [channel]. — [Named spokesperson, title]"
            </div>
            <div className="mt-3"><CopyButton testid="b12-copy" getText={() => `CRISIS PLAYBOOK — FIRST 60 MINUTES\n\n${B12_TIMELINE.map(([t, a]) => `${t}: ${a}`).join("\n")}\n\nHOLDING STATEMENT\n"We are aware of [incident] affecting [product/batch]..."`} /></div>
          </ToolSection>
        </div>
      </div>
    </div>
  );
}
